/**
 * The bell in every page header: unread count on the icon; tapping opens "Tilkynningar", one line per
 * thread with something new (an event's message board or a chat), newest first. Tapping a line opens the
 * thread, which marks it read; "Merkja allt lesið" marks every listed thread read at once.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, MessageCircle, MessagesSquare } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationItemDTO } from '../../shared/dto/chat';
import { useServerNow } from '../api/calendar';
import { markAllNotificationsRead, useNotifications } from '../api/notifications';
import { useMe } from '../auth/AuthProvider';
import { Avatar } from '../components/people';
import { useToast } from '../components/toast';
import { Button, cx, EmptyState, ErrorBox, Sheet, Spinner } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { formatRelativeTime } from '../lib/dates';
import { badgeText, displayNames, notificationHref, notificationTitle } from './format';

/** Up to two authors' avatars, overlapping, with the thread kind in the corner. */
function ItemAvatars({ item }: { item: NotificationItemDTO }) {
  const shown = item.authors.slice(0, 2);
  const KindIcon = item.kind === 'event' ? MessageCircle : MessagesSquare;
  return (
    <span className="relative h-12 w-14 shrink-0" aria-hidden>
      {shown[1] ? (
        <Avatar userId={shown[1].id} name={shown[1].fullName} rev={shown[1].avatarRev} section={shown[1].section} size={36} className="absolute top-0 right-0 ring-2 ring-[var(--surface)]" />
      ) : null}
      {shown[0] ? (
        <Avatar
          userId={shown[0].id}
          name={shown[0].fullName}
          rev={shown[0].avatarRev}
          section={shown[0].section}
          size={shown[1] ? 36 : 44}
          className={cx('absolute ring-2 ring-[var(--surface)]', shown[1] ? 'bottom-0 left-0' : 'top-0.5 left-1')}
        />
      ) : null}
      <span className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full bg-[var(--accent)] text-white ring-2 ring-[var(--surface)]">
        <KindIcon className="size-3.5" />
      </span>
    </span>
  );
}

function NotificationRow({ item, now, onOpen }: { item: NotificationItemDTO; now: number; onOpen: (item: NotificationItemDTO) => void }) {
  const t = useT();
  const { loc } = useI18n();
  const title = notificationTitle(item, t, loc);
  const who = displayNames([item.preview.authorName])[0];
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="flex min-h-16 w-full items-start gap-3 rounded-2xl px-2 py-3 text-left transition hover:bg-[var(--accent-soft)] active:bg-[var(--accent-soft)]"
      >
        <ItemAvatars item={item} />
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] leading-snug font-semibold">{title}</span>
          <span className="mt-0.5 line-clamp-2 block text-[15px] break-words text-[var(--ink-2)]">
            {who}: {item.preview.body}
          </span>
          <span className="mt-0.5 block text-[13px] text-[var(--muted)]">{formatRelativeTime(item.latestAt, now, loc)}</span>
        </span>
        <span
          className="mt-1 inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-[var(--danger)] px-1.5 text-[13px] font-bold text-white"
          aria-label={t('notify.new_count', { count: item.unread })}
        >
          {badgeText(item.unread)}
        </span>
      </button>
    </li>
  );
}

function NotificationsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useNotifications(useMe());
  const now = useServerNow(30_000);
  const [busy, setBusy] = useState(false);
  const items = q.data?.items ?? [];

  const openItem = (item: NotificationItemDTO) => {
    onClose();
    void navigate(notificationHref(item));
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await markAllNotificationsRead(qc, items);
    } catch {
      toast.show(t('notify.mark_all_error'), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('notify.title')}
      footer={
        items.length > 0 ? (
          <>
            <Button variant="secondary" block onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button block busy={busy} onClick={() => void markAll()}>
              {t('notify.mark_all')}
            </Button>
          </>
        ) : undefined
      }
    >
      {items.length > 0 ? (
        <ul className="-mx-2 flex flex-col" aria-label={t('notify.title')}>
          {items.map((it) => (
            <NotificationRow key={`${it.kind}:${it.id}`} item={it} now={now} onOpen={openItem} />
          ))}
        </ul>
      ) : q.isPending && q.fetchStatus === 'fetching' ? (
        <div className="py-10 text-center">
          <Spinner label={t('common.loading')} />
        </div>
      ) : q.isError && !q.data ? (
        <div className="py-4">
          <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
            {t('notify.load_error')}
          </ErrorBox>
        </div>
      ) : (
        <EmptyState icon={BellOff} title={t('notify.empty_title')}>
          {t('notify.empty')}
        </EmptyState>
      )}
    </Sheet>
  );
}

export function NotificationBell() {
  const t = useT();
  const q = useNotifications(useMe());
  const [open, setOpen] = useState(false);
  const total = q.data?.total ?? 0;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={total > 0 ? t('notify.open_unread', { count: total }) : t('notify.open')}
        title={t('notify.title')}
        className="relative -mr-1.5 ml-auto inline-flex size-11 shrink-0 items-center justify-center rounded-full text-[var(--ink-2)] transition hover:bg-[var(--accent-soft)]"
      >
        <Bell className="size-6" aria-hidden />
        {total > 0 ? (
          <span aria-hidden className="absolute top-0.5 right-0 h-5 min-w-5 rounded-full bg-[var(--danger)] px-1 text-center text-[12px] leading-5 font-bold text-white">
            {badgeText(total)}
          </span>
        ) : null}
      </button>
      <NotificationsSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}
